import {useState} from 'react';
import {Link, useLocation} from 'react-router';
import {useAuth} from '@common/auth/use-auth';
import {Trans} from '@ui/i18n/trans';
import {EditIcon} from '@ui/icons/material/Edit';
import {PaletteIcon} from '@ui/icons/material/Palette';
import {StyleIcon} from '@ui/icons/material/Style';
import {PreviewIcon} from '@ui/icons/material/Preview';
import {TuneIcon} from '@ui/icons/material/Tune';
import {CloseIcon} from '@ui/icons/material/Close';
import {getAppearance} from '../appearance-data';

/**
 * Floating appearance control shown to admins on public-facing pages. It sits as
 * a round button ABOVE the live-chat launcher (bottom corner) and, when opened,
 * expands into a small panel with quick links into the appearance admin
 * (blocks builder / theme options / themes / live editor). Hidden on the admin
 * and dashboard areas (where those tools already live). Renders nothing for
 * non-admins.
 */
export function AppearanceAdminBar() {
  const {hasPermission} = useAuth();
  const {pathname} = useLocation();
  const [open, setOpen] = useState(false);

  if (!hasPermission('admin')) return null;
  // Don't show inside the back-office areas.
  if (/^\/(admin|dashboard|student)\b/.test(pathname)) return null;

  const theme = getAppearance().theme;

  const link =
    'flex items-center gap-10 rounded-lg px-12 py-8 text-xs font-medium text-white/90 hover:bg-white/15';

  return (
    // Positioned above the live-chat launcher (which sits at the very bottom
    // corner). z-index sits just below the chat widget so it never covers it.
    <div className="fixed bottom-90 z-[80] flex flex-col items-end gap-10 ltr:right-16 rtl:left-16">
      {/* Expanded panel with the related appearance links + live editor. */}
      {open && (
        <div
          className="flex w-200 flex-col gap-2 rounded-xl p-8 shadow-2xl"
          style={{backgroundColor: '#111827', color: '#fff'}}
        >
          <div className="flex items-center gap-8 px-12 pb-6 pt-4">
            <TuneIcon size="xs" className="text-white/70" />
            <span className="text-xs font-semibold">
              <Trans message="Appearance" />
              {theme ? (
                <span className="ms-6 text-white/60">· {theme.name}</span>
              ) : null}
            </span>
          </div>

          <Link to="/admin/appearance/builder" className={link}>
            <EditIcon size="xs" /> <Trans message="Edit blocks" />
          </Link>
          <Link to="/admin/appearance/live" className={link}>
            <PreviewIcon size="xs" /> <Trans message="Live Editor" />
          </Link>
          <Link to="/admin/appearance/theme-options" className={link}>
            <PaletteIcon size="xs" /> <Trans message="Theme Options" />
          </Link>
          <Link to="/admin/appearance/themes" className={link}>
            <StyleIcon size="xs" /> <Trans message="Themes & Presets" />
          </Link>
        </div>
      )}

      {/* The floating toggle button itself. */}
      <button
        onClick={() => setOpen(prev => !prev)}
        aria-label="Appearance"
        aria-expanded={open}
        title="Appearance"
        className="flex h-48 w-48 items-center justify-center rounded-full shadow-2xl transition-transform hover:scale-105"
        style={{backgroundColor: '#111827', color: '#fff'}}
      >
        {open ? <CloseIcon size="sm" /> : <TuneIcon size="sm" />}
      </button>
    </div>
  );
}
